import React from 'react';
import './css/AgePricingEditor.css';

const MEAL_SLOTS = ['Breakfast', 'Lunch', 'Snacks', 'Dinner'];

const DEFAULT_TIMES_24 = {
    Breakfast: '09:00',
    Lunch: '13:00',
    Snacks: '16:00',
    Dinner: '20:00',
};

const DEFAULT_CUTOFF_24 = {
    Breakfast: '08:00',
    Lunch: '12:00',
    Snacks: '15:00',
    Dinner: '19:00',
};

function normalizeEntry(raw, mealSlot) {
    if (!raw || typeof raw !== 'object') {
        return {
            mealSlot,
            enabled: true,
            deliveryTime: DEFAULT_TIMES_24[mealSlot],
            orderCutoffTime: DEFAULT_CUTOFF_24[mealSlot],
        };
    }
    const deliveryTime = String(
        raw.time24 ??
            raw.time24h ??
            raw.deliveryTime ??
            raw.delivery_time ??
            DEFAULT_TIMES_24[mealSlot],
    ).slice(0, 5);
    const orderCutoffTime = String(
        raw.cutoffTime24 ??
            raw.orderCutoffTime ??
            raw.order_cutoff_time ??
            raw.cutoffTime ??
            DEFAULT_CUTOFF_24[mealSlot],
    ).slice(0, 5);
    const enabled =
        raw.enabled === undefined && raw.Enabled === undefined
            ? true
            : Boolean(raw.enabled ?? raw.Enabled);
    return {
        mealSlot,
        enabled,
        deliveryTime,
        orderCutoffTime,
    };
}

function buildRowsFromApiPayload(payload) {
    const map = payload && typeof payload === 'object' ? payload : {};
    return MEAL_SLOTS.map((mealSlot) => normalizeEntry(map[mealSlot], mealSlot));
}

/**
 * Meal delivery + order cutoff times per slot (Jr Eats / Work Eats defaults).
 */
export default function MealDeliveryTimesEditor({ value, onChange, disabled = false, hideHeader = false }) {
    const rows = Array.isArray(value) && value.length > 0
        ? MEAL_SLOTS.map((mealSlot) => {
            const found = value.find(
                (row) => String(row?.mealSlot ?? row?.meal ?? '').toLowerCase() === mealSlot.toLowerCase(),
            );
            return normalizeEntry(found, mealSlot);
        })
        : buildRowsFromApiPayload(value);

    const updateRow = (mealSlot, patch) => {
        const next = rows.map((row) =>
            row.mealSlot === mealSlot ? { ...row, ...patch } : row,
        );
        onChange?.(next);
    };

    return (
        <div className="age-pricing-editor meal-delivery-editor">
            {!hideHeader ? (
            <div className="age-pricing-editor__header">
                <label className="age-pricing-editor__title">Meal delivery & order cutoff</label>
                <p className="age-pricing-editor__hint">
                    Enable only meals you serve. Delivery time is when food arrives; order cutoff is
                    the latest time customers can place that meal order. Unchecked meals are hidden
                    in Work Eats. Used for Junior Eats and as the Work Eats default when no
                    office-specific times are set.
                </p>
            </div>
            ) : null}
            <div className="age-pricing-editor__table-wrap">
                <table className="age-pricing-editor__table">
                    <thead>
                        <tr>
                            <th>Offer</th>
                            <th>Meal</th>
                            <th>Order cutoff</th>
                            <th>Delivery time</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.mealSlot} style={{ opacity: row.enabled ? 1 : 0.55 }}>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(row.enabled)}
                                        disabled={disabled}
                                        onChange={(e) =>
                                            updateRow(row.mealSlot, { enabled: e.target.checked })
                                        }
                                        aria-label={`Offer ${row.mealSlot}`}
                                    />
                                </td>
                                <td>{row.mealSlot}</td>
                                <td>
                                    <input
                                        type="time"
                                        className="add-menu-form-field age-pricing-editor__input"
                                        value={row.orderCutoffTime}
                                        disabled={disabled || !row.enabled}
                                        onChange={(e) =>
                                            updateRow(row.mealSlot, {
                                                orderCutoffTime: e.target.value,
                                            })
                                        }
                                    />
                                </td>
                                <td>
                                    <input
                                        type="time"
                                        className="add-menu-form-field age-pricing-editor__input"
                                        value={row.deliveryTime}
                                        disabled={disabled || !row.enabled}
                                        onChange={(e) =>
                                            updateRow(row.mealSlot, { deliveryTime: e.target.value })
                                        }
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export { buildRowsFromApiPayload, MEAL_SLOTS, DEFAULT_TIMES_24, DEFAULT_CUTOFF_24 };
